var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ProcessSearchContext from '../context/process-search-context';
import { searchProcessData_AC } from '../store/actions/process';
import usePagination from './usePagination';
var validateQueryModels = function (queryModels) {
    if (!queryModels) {
        throw new Error('Query model is not defined in usePeach');
    }
    if (!(queryModels instanceof Array)) {
        throw new Error('Query models is not valid. Must be array.');
    }
    queryModels.forEach(function (model) {
        if (!model.alias || !model.label) {
            console.error('Query Model', { queryModels: queryModels, model: model });
            throw new Error('Query model is not valid. Must contain alias & label');
        }
    });
};
var commonFields = {
    textField: {
        like: {
            selector: 'like',
            class: 'condition',
        },
        lessThan: {
            selector: '<',
            class: 'condition',
        },
        startsWith: {
            selector: 'startsWith',
            class: 'condition',
        },
        lessThanEqualTo: {
            selector: '<=',
            class: 'condition',
        },
        contains: {
            selector: 'contains',
            class: 'condition',
        },
        endsWith: {
            selector: 'endsWith',
            class: 'condition',
        },
        equals: {
            selector: '==',
            class: 'condition',
        },
        greaterThan: {
            selector: '>',
            class: 'condition',
        },
        greaterThanEqualTo: {
            selector: '>=',
            class: 'condition',
        },
    },
    date: {
        lessThan: {
            selector: '<',
            type: 'calendar',
            class: 'condition',
        },
        notEqualTo: {
            selector: '!=',
            type: 'calendar',
            class: 'condition',
        },
        lessThanEqualTo: {
            selector: '<=',
            type: 'calendar',
            class: 'condition',
        },
        equals: {
            selector: '==',
            type: 'calendar',
            class: 'condition',
        },
        greaterThan: {
            selector: '>',
            type: 'calendar',
            class: 'condition',
        },
        greaterThanEqualTo: {
            selector: '>=',
            type: 'calendar',
            class: 'condition',
        },
    },
    boolean: {
        true: {
            selector: 'true',
        },
        false: {
            selector: 'false',
        },
    },
    calendar: {
        datePicker: {
            class: 'calendar',
        },
    },
};
var buildQueryInstance = function (_a) {
    var query = _a.query, fields = _a.fields;
    try {
        var options = query.split(' ');
        var requestOption = {
            selector: '',
        };
        var selectedConcept = {};
        if (options.length >= 1) {
            selectedConcept = fields[fields.conceptKey][options[0].trim()];
            requestOption.selector = selectedConcept.selector;
            if (selectedConcept.type === 'boolean') {
                var value = options[1];
                if (value === false || value === 'false') {
                    requestOption.comparison = 'not';
                }
            }
            else if (selectedConcept.type === 'textField' ||
                selectedConcept.type === 'date') {
                var conditionalOptions = fields[selectedConcept.type];
                var conditionalObject = conditionalOptions[options[1].trim()];
                requestOption.comparison = conditionalObject.selector;
                options.splice(0, 2);
                requestOption.value = options.join(' ');
            }
            else if (selectedConcept.type.startsWith('cg')) {
                options.splice(0, 1);
                var value = options.join(' ');
                requestOption.value = fields[selectedConcept.type][value].selector;
            }
            else {
                options.splice(0, 1);
                var value = options.join(' ');
                requestOption.value = value;
            }
        }
        return requestOption;
    }
    catch (error) {
        console.log('invalid query instance');
        return undefined;
    }
};
var useSearch = function (peachProps, paginationProps) {
    var _a = peachProps.initialQuery, initialQuery = _a === void 0 ? '' : _a, queryModels = peachProps.queryModels, _b = peachProps.searchType, searchType = _b === void 0 ? 'raw' : _b, _c = peachProps.searchRequestProps, searchRequestProps = _c === void 0 ? {} : _c, defaultQueryModel = peachProps.defaultQueryModel, onSelectModel = peachProps.onSelectModel, fields = peachProps.fields;
    var _d = useTranslation(), i18n = _d.i18n, t = _d.t;
    var choiceGroups = useSelector(function (state) { return state; }).choiceGroups;
    validateQueryModels(queryModels);
    var dispatch = useDispatch();
    var psContext = useContext(ProcessSearchContext);
    var _e = useState({
        query: initialQuery,
        selectedModel: defaultQueryModel || queryModels[0],
        searching: false,
        results: [],
        error: undefined,
        savedSearchParams: undefined,
        report: {
            id: undefined,
            retrievingId: false,
            error: undefined,
        },
        transformedFields: undefined,
    }), state = _e[0], setState = _e[1];
    var translateToLabels = function (_a) {
        var fieldObjects = _a.fieldObjects, t = _a.t, type = _a.type;
        var values = {};
        Object.keys(fieldObjects).forEach(function (key) {
            var label = t("search." + type + "." + key);
            values[label] = __assign(__assign({}, fieldObjects[key]), { label: label });
        });
        return values;
    };
    var getChoiceGroupOptions = function (key) {
        var values = {};
        var selectedChoiceGroup = choiceGroups[key];
        if (selectedChoiceGroup && selectedChoiceGroup.nodes) {
            selectedChoiceGroup.nodes.forEach(function (node) {
                var label = node.name[i18n.language];
                values[label] = {
                    selector: node.alias,
                    label: label,
                };
            });
        }
        console.warn('[Choice group not available in store]', key);
        return values;
    };
    var findAndReplaceByType = function (_a) {
        var key = _a.key, fields = _a.fields, t = _a.t;
        if (fields[key]['type'] === 'choiceGroup') {
            return getChoiceGroupOptions(key);
        }
        switch (key) {
            case 'concepts':
            case 'textField':
            case 'date':
            case 'boolean':
                return translateToLabels({
                    fieldObjects: fields[key],
                    t: t,
                    type: key,
                });
            default:
                return fields[key];
        }
    };
    var pagination = usePagination(paginationProps);
    var updateQuery = function (query) {
        return setState(function (prevState) { return (__assign(__assign({}, prevState), { query: query })); });
    };
    var selectModel = function (model) {
        return setState(function (prevState) { return (__assign(__assign({}, prevState), { selectedModel: model })); });
    };
    var buildAndQueries = function (_a) {
        var query = _a.query, fields = _a.fields;
        if (!query) {
            return undefined;
        }
        try {
            var querySlices = query.split(',');
            var queryRequests = querySlices.map(function (querySlice) {
                var option = buildQueryInstance({ query: querySlice.trim(), fields: fields });
                return option;
            });
            return {
                type: 'and',
                value: queryRequests,
            };
        }
        catch (error) {
            console.log('invalid query');
        }
        return {};
    };
    var performSearch = function (oldSearch) {
        var query = state.query, selectedModel = state.selectedModel, savedSearchParams = state.savedSearchParams, transformedFields = state.transformedFields;
        var page = oldSearch ? pagination.state.current : 1;
        if (!oldSearch) {
            pagination.updatePagination(page, pagination.state.pageSize, false);
        }
        var rawQuery = buildAndQueries({ query: query, fields: transformedFields });
        var searchParams = oldSearch
            ? savedSearchParams
            : __assign({ modelAlias: selectedModel.alias, language: i18n.language, queries: __spreadArrays((rawQuery
                    ? [
                        {
                            type: searchType,
                            query: rawQuery,
                        },
                    ]
                    : []), (selectedModel.dsl
                    ? selectedModel.dsl.map(function (dsl) { return ({
                        type: 'dsl',
                        query: dsl,
                    }); })
                    : []), (selectedModel.peach
                    ? selectedModel.peach.map(function (peach) { return ({
                        type: 'peach',
                        query: peach,
                    }); })
                    : []), (selectedModel.raw ? selectedModel.raw : [])) }, searchRequestProps);
        var searchRequest = __assign(__assign({}, searchParams), { subscribe: false, page: {
                number: page,
                perPage: pagination.state.pageSize,
            } });
        console.log('[useSearch] performSearch:', {
            state: state,
            pagination: pagination,
            peachProps: peachProps,
            paginationProps: paginationProps,
            searchRequest: searchRequest,
            page: page,
            searchParams: searchParams,
        });
        setState(function (prevState) { return (__assign(__assign(__assign({}, prevState), { searching: true, results: {}, error: undefined, savedSearchParams: searchParams }), (oldSearch
            ? {}
            : {
                report: {
                    id: undefined,
                    retrievingId: false,
                    error: undefined,
                },
            }))); });
        dispatch(searchProcessData_AC(searchRequest.modelAlias, [searchRequest], psContext.name));
    };
    useEffect(function () {
        onSelectModel && onSelectModel(state.selectedModel);
    }, [state.selectedModel]);
    useEffect(function () {
        if (pagination.state.changed) {
            performSearch(true);
        }
    }, [pagination.state.changed]);
    useEffect(function () {
        performSearch('');
    }, []);
    useEffect(function () {
        var values = {};
        var allFields = __assign(__assign({}, fields), commonFields);
        Object.keys(allFields).forEach(function (key) {
            values[key] = findAndReplaceByType({ key: key, fields: allFields, t: t });
        });
        setState(function (prevState) { return (__assign(__assign({}, prevState), { transformedFields: values })); });
    }, [t]);
    return {
        search: {
            updateQuery: updateQuery,
            selectModel: selectModel,
            performSearch: performSearch,
            state: state,
        },
        pagination: pagination,
    };
};
export default useSearch;
//# sourceMappingURL=useSearch.js.map